import { API_BASE_URL } from '../config/api';

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN ?? '';
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID ?? '';
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE ?? 'santtai-api';
const DB_CONNECTION =
  import.meta.env.VITE_AUTH0_DB_CONNECTION ??
  'Username-Password-Authentication';

const TOKEN_SCOPE = 'openid profile email offline_access';
const SESSION_STORAGE_KEY = 'santtai.auth.session';

export type AuthUser = {
  sub?: string;
  name?: string;
  email?: string;
  picture?: string;
  nickname?: string;
  gender?: string;
  phone_number?: string;
};

export type AuthSession = {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt: number;
  user: AuthUser;
};

type TokenResponse = {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope?: string;
};

type Auth0ErrorBody = {
  error?: string;
  error_description?: string;
  description?: string;
  message?: string;
};

type BackendErrorBody = {
  error?: string;
  error_description?: string;
  message?: string;
};

export const isAuthConfigured = Boolean(AUTH0_DOMAIN && AUTH0_CLIENT_ID);

const getAuthBaseUrl = () => `https://${AUTH0_DOMAIN}`;

const parseJwtPayload = (token: string): AuthUser => {
  const [, payload] = token.split('.');
  if (!payload) {
    return {};
  }

  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const decoded = atob(normalized);
  return JSON.parse(decoded) as AuthUser;
};

const toSession = (tokenResponse: TokenResponse): AuthSession => {
  const user = parseJwtPayload(tokenResponse.id_token);
  return {
    accessToken: tokenResponse.access_token,
    idToken: tokenResponse.id_token,
    refreshToken: tokenResponse.refresh_token,
    expiresAt: Date.now() + tokenResponse.expires_in * 1000,
    user,
  };
};

const readAuth0Error = async (response: Response): Promise<string> => {
  try {
    const body = (await response.json()) as Auth0ErrorBody;
    return (
      body.error_description ||
      body.description ||
      body.message ||
      body.error ||
      `Authentication failed (${response.status})`
    );
  } catch {
    return `Authentication failed (${response.status})`;
  }
};

const readBackendError = async (response: Response): Promise<string> => {
  try {
    const body = (await response.json()) as BackendErrorBody;
    return (
      body.error ||
      body.error_description ||
      body.message ||
      `Authentication failed (${response.status})`
    );
  } catch {
    return `Authentication failed (${response.status})`;
  }
};

const postAuth0Form = async (
  path: string,
  body: Record<string, string>,
): Promise<Response> => {
  return fetch(`${getAuthBaseUrl()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(body),
  });
};

export const loadStoredSession = (): AuthSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
};

export const saveSession = (session: AuthSession): void => {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

export const clearSession = (): void => {
  localStorage.removeItem(SESSION_STORAGE_KEY);
};

export const loginWithPassword = async (
  email: string,
  password: string,
): Promise<AuthSession> => {
  const response = await postAuth0Form('/oauth/token', {
    grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
    username: email,
    password,
    audience: AUTH0_AUDIENCE,
    scope: TOKEN_SCOPE,
    client_id: AUTH0_CLIENT_ID,
    realm: DB_CONNECTION,
  });

  if (!response.ok) {
    throw new Error(await readAuth0Error(response));
  }

  const tokenResponse = (await response.json()) as TokenResponse;
  const session = toSession(tokenResponse);
  saveSession(session);
  return session;
};

export const signUpWithPassword = async (
  email: string,
  password: string,
  fullName: string,
): Promise<AuthSession> => {
  const response = await fetch(`${getAuthBaseUrl()}/dbconnections/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      client_id: AUTH0_CLIENT_ID,
      email,
      password,
      connection: DB_CONNECTION,
      name: fullName,
    }),
  });

  if (!response.ok) {
    throw new Error(await readAuth0Error(response));
  }

  return loginWithPassword(email, password);
};

export const requestPasswordReset = async (email: string): Promise<void> => {
  const response = await fetch(
    `${getAuthBaseUrl()}/dbconnections/change_password`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: AUTH0_CLIENT_ID,
        email,
        connection: DB_CONNECTION,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(await readAuth0Error(response));
  }
};

export const startPasswordlessEmail = async (
  email: string,
  send: 'code' | 'link' = 'code',
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/auth/passwordless/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, send }),
  });

  if (!response.ok) {
    throw new Error(await readBackendError(response));
  }
};

export const verifyPasswordlessOtp = async (
  email: string,
  otp: string,
): Promise<AuthSession> => {
  const response = await fetch(`${API_BASE_URL}/auth/passwordless/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, otp }),
  });

  if (!response.ok) {
    throw new Error(await readBackendError(response));
  }

  const tokenResponse = (await response.json()) as {
    accessToken?: string;
    idToken?: string;
    refreshToken?: string;
    expiresIn?: number;
    access_token?: string;
    id_token?: string;
    refresh_token?: string;
    expires_in?: number;
  };

  const normalized: TokenResponse = {
    access_token: tokenResponse.accessToken ?? tokenResponse.access_token ?? '',
    id_token: tokenResponse.idToken ?? tokenResponse.id_token ?? '',
    refresh_token:
      tokenResponse.refreshToken ?? tokenResponse.refresh_token ?? undefined,
    expires_in: tokenResponse.expiresIn ?? tokenResponse.expires_in ?? 7200,
    token_type: 'Bearer',
  };

  if (!normalized.access_token || !normalized.id_token) {
    throw new Error('Authentication succeeded but tokens were missing.');
  }

  const session = toSession(normalized);
  saveSession(session);
  return session;
};

export const refreshSession = async (
  refreshToken: string,
): Promise<AuthSession> => {
  const response = await postAuth0Form('/oauth/token', {
    grant_type: 'refresh_token',
    client_id: AUTH0_CLIENT_ID,
    refresh_token: refreshToken,
  });

  if (!response.ok) {
    throw new Error(await readAuth0Error(response));
  }

  const tokenResponse = (await response.json()) as TokenResponse;
  const previous = loadStoredSession();
  const session = toSession({
    ...tokenResponse,
    refresh_token: tokenResponse.refresh_token ?? previous?.refreshToken,
  });
  saveSession(session);
  return session;
};

export const getValidAccessToken = async (): Promise<string> => {
  const session = loadStoredSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const isExpired = session.expiresAt <= Date.now() + 60_000;
  if (!isExpired) {
    return session.accessToken;
  }

  if (!session.refreshToken) {
    clearSession();
    throw new Error('Session expired');
  }

  const refreshed = await refreshSession(session.refreshToken);
  return refreshed.accessToken;
};

export const parseCallbackHashSession = (): AuthSession | null => {
  const hash = window.location.hash?.replace(/^#/, '');
  if (!hash) {
    return null;
  }

  const params = new URLSearchParams(hash);
  const accessToken = params.get('access_token');
  const idToken = params.get('id_token');
  const refreshToken = params.get('refresh_token') ?? undefined;
  const expiresIn = Number(params.get('expires_in') ?? '7200');

  if (!accessToken || !idToken) {
    return null;
  }

  const session: AuthSession = {
    accessToken,
    idToken,
    refreshToken,
    expiresAt: Date.now() + expiresIn * 1000,
    user: parseJwtPayload(idToken),
  };
  saveSession(session);
  return session;
};
